/**
 * TagInput Component
 * Chip-based tag input with autocomplete from existing user tags.
 * - Type to search/filter existing tags or create new ones
 * - Press Enter, comma, or tap a suggestion to add a tag
 * - Tap X on a chip to remove it
 * - Shows existing tags as suggestions sorted by usage
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { colors } from '../../theme/colors';
import { typography } from '../../theme/typography';
import { spacing } from '../../theme/spacing';
import { budgetApi } from '../../api/endpoints/budget';
import logger from '../../utils/logger';

interface TagSuggestion {
  name: string;
  color: string | null;
  usage_count: number;
}

interface TagInputProps {
  tags: string[];
  onChange: (tags: string[]) => void;
  maxTags?: number;
}

export default function TagInput({ tags, onChange, maxTags = 20 }: TagInputProps) {
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState<TagSuggestion[]>([]);
  const [allTags, setAllTags] = useState<TagSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<TextInput>(null);
  const hasFetchedRef = useRef(false);

  // Fetch all user tags once on mount
  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchTags();
  }, []);

  const fetchTags = async () => {
    try {
      setLoading(true);
      const result = await budgetApi.getUserTags();
      setAllTags(result);
    } catch (err) {
      logger.error('[TagInput] Error fetching tags:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filter suggestions based on input
  useEffect(() => {
    const query = inputValue.trim().toLowerCase();
    if (!query) {
      // Show all unused tags when input is empty but focused
      setSuggestions(allTags.filter(t => !tags.includes(t.name)));
    } else {
      setSuggestions(
        allTags.filter(t =>
          t.name.includes(query) && !tags.includes(t.name)
        )
      );
    }
  }, [inputValue, allTags, tags]);

  const addTag = useCallback((tagName: string) => {
    const normalized = tagName.trim().toLowerCase();
    if (!normalized || tags.includes(normalized) || tags.length >= maxTags) return;
    onChange([...tags, normalized]);
    setInputValue('');
  }, [tags, onChange, maxTags]);

  const removeTag = useCallback((index: number) => {
    onChange(tags.filter((_, i) => i !== index));
  }, [tags, onChange]);

  const handleTextChange = (text: string) => {
    // If user types a comma or semicolon, treat it as a tag separator
    if (text.includes(',') || text.includes(';')) {
      const parts = text.split(/[,;]/);
      // Add all complete parts as tags
      parts.slice(0, -1).forEach(part => {
        const trimmed = part.trim().toLowerCase();
        if (trimmed) addTag(trimmed);
      });
      // Keep the last part (after the last separator) as current input
      setInputValue(parts[parts.length - 1]);
      return;
    }
    setInputValue(text);
    setShowSuggestions(true);
  };

  const handleSubmitEditing = () => {
    if (inputValue.trim()) {
      addTag(inputValue);
    }
  };

  const handleFocus = () => {
    setShowSuggestions(true);
  };

  const handleBlur = () => {
    // Delay hiding so tap on suggestion registers
    setTimeout(() => setShowSuggestions(false), 200);
    // Auto-add what's typed if non-empty
    if (inputValue.trim()) {
      addTag(inputValue);
    }
  };

  const handleSuggestionPress = (tagName: string) => {
    addTag(tagName);
    inputRef.current?.focus();
  };

  const showCreateOption = inputValue.trim() &&
    !tags.includes(inputValue.trim().toLowerCase()) &&
    !suggestions.some(s => s.name === inputValue.trim().toLowerCase());

  const visibleSuggestions = suggestions.slice(0, 8);

  return (
    <View style={styles.container}>
      {/* Selected tags as chips */}
      <View style={styles.chipRow}>
        {tags.map((tag, index) => (
          <View key={tag} style={styles.chip}>
            <Text style={styles.chipText}>{tag}</Text>
            <TouchableOpacity
              onPress={() => removeTag(index)}
              hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
            >
              <Icon name="close-circle" size={14} color={colors.primary} />
            </TouchableOpacity>
          </View>
        ))}

        {/* Inline input */}
        {tags.length < maxTags && (
          <TextInput
            ref={inputRef}
            style={styles.input}
            value={inputValue}
            onChangeText={handleTextChange}
            onSubmitEditing={handleSubmitEditing}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={tags.length === 0 ? 'Ajouter un tag...' : '+'}
            placeholderTextColor={colors.textSecondary}
            returnKeyType="done"
            blurOnSubmit={false}
            autoCapitalize="none"
            autoCorrect={false}
          />
        )}
      </View>

      {/* Hint text */}
      {tags.length === 0 && !showSuggestions && (
        <Text style={styles.hint}>Sépare les tags avec Entrée ou virgule</Text>
      )}

      {/* Suggestions dropdown */}
      {showSuggestions && (visibleSuggestions.length > 0 || showCreateOption) && (
        <View style={styles.suggestionsContainer}>
          {loading && (
            <View style={styles.loadingRow}>
              <ActivityIndicator size="small" color={colors.primary} />
            </View>
          )}

          {/* Create new tag option */}
          {showCreateOption && (
            <TouchableOpacity
              style={styles.suggestionRow}
              onPress={() => handleSuggestionPress(inputValue.trim().toLowerCase())}
            >
              <Icon name="plus-circle-outline" size={16} color={colors.primary} />
              <Text style={styles.suggestionText}>
                Créer <Text style={styles.suggestionBold}>"{inputValue.trim().toLowerCase()}"</Text>
              </Text>
            </TouchableOpacity>
          )}

          {/* Existing tag suggestions */}
          {visibleSuggestions.map(suggestion => (
            <TouchableOpacity
              key={suggestion.name}
              style={styles.suggestionRow}
              onPress={() => handleSuggestionPress(suggestion.name)}
            >
              <Icon name="tag-outline" size={16} color={colors.textSecondary} />
              <Text style={styles.suggestionText}>{suggestion.name}</Text>
              <Text style={styles.suggestionCount}>{suggestion.usage_count}x</Text>
            </TouchableOpacity>
          ))}
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginBottom: spacing.md,
  },
  chipRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    paddingHorizontal: spacing.sm,
    paddingVertical: 6,
    minHeight: 40,
    gap: 6,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: colors.primary + '15',
    paddingHorizontal: spacing.sm,
    paddingVertical: 4,
    borderRadius: 16,
  },
  chipText: {
    fontSize: typography.fontSize.xs,
    color: colors.primary,
    fontWeight: typography.fontWeight.medium as '500',
  },
  input: {
    flex: 1,
    minWidth: 80,
    fontSize: typography.fontSize.sm,
    color: colors.text,
    paddingVertical: 2,
    paddingHorizontal: 4,
  },
  hint: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    marginTop: 4,
    marginLeft: 2,
  },
  suggestionsContainer: {
    backgroundColor: colors.background,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    marginTop: 4,
    maxHeight: 200,
    overflow: 'hidden',
  },
  loadingRow: {
    padding: spacing.sm,
    alignItems: 'center',
  },
  suggestionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    gap: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.border,
  },
  suggestionText: {
    flex: 1,
    fontSize: typography.fontSize.sm,
    color: colors.text,
  },
  suggestionBold: {
    fontWeight: typography.fontWeight.semibold as '600',
    color: colors.primary,
  },
  suggestionCount: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
  },
});
